import { create } from "zustand";
import { fetchApi } from "@/lib/api";
import { captureException } from "@/lib/analytics";

import {
  Operation,
  CreateOperationParams,
  UpdateOperationParams,
  ListOperationsResponseSchema,
  GetOperationResponseSchema,
  CreateOperationResponseSchema,
  UpdateOperationResponseSchema,
} from "@forgepoint/shared";

interface OperationLoadingState {
  isLoadingGet: boolean;
  isLoadingSave: boolean;
  isLoadingDelete: boolean;
  isLoadingArchive: boolean;
  getError: string | null;
  saveError: string | null;
  deleteError: string | null;
}

interface OperationsStore {
  operations: Map<string, Operation>;
  operationLoadingStates: Map<string, OperationLoadingState>;
  listLoading: boolean;
  listError: string | null;

  fetchOperations: (params?: {
    search?: string;
    limit?: number;
    offset?: number;
    includeArchived?: boolean;
  }) => Promise<{ ids: string[]; total: number }>;
  fetchOperation: (id: string) => Promise<void>;
  createOperation: (
    data: CreateOperationParams,
  ) => Promise<{ operation: Operation | null; error: string | null }>;
  updateOperation: (
    id: string,
    data: UpdateOperationParams,
  ) => Promise<{ operation: Operation | null; error: string | null }>;
  deleteOperation: (id: string) => Promise<boolean>;
  setOperationArchived: (
    id: string,
    archived: boolean,
  ) => Promise<{ operation: Operation | null; error: string | null }>;
}

const getInitialLoadingState = (): OperationLoadingState => ({
  isLoadingGet: false,
  isLoadingSave: false,
  isLoadingDelete: false,
  isLoadingArchive: false,
  getError: null,
  saveError: null,
  deleteError: null,
});

export const useOperationsStore = create<OperationsStore>((set) => ({
  operations: new Map(),
  operationLoadingStates: new Map(),
  listLoading: false,
  listError: null,

  fetchOperations: async (params?: {
    search?: string;
    limit?: number;
    offset?: number;
    includeArchived?: boolean;
  }) => {
    set({ listLoading: true, listError: null });

    try {
      const searchParams = new URLSearchParams();
      if (params?.search) {
        searchParams.set("search", params.search);
      }
      if (params?.limit !== undefined) {
        searchParams.set("limit", String(params.limit));
      }
      if (params?.offset !== undefined) {
        searchParams.set("offset", String(params.offset));
      }
      if (params?.includeArchived) {
        searchParams.set("includeArchived", "true");
      }
      const queryString = searchParams.toString();
      const url = queryString ? `operations?${queryString}` : "operations";
      const response = await fetchApi(url, "GET");

      if (response.ok && response.data) {
        // Parse dates from strings before validation
        const rawData = response.data as {
          operations: Array<{
            createdAt?: string;
            updatedAt?: string;
            [key: string]: unknown;
          }>;
        };
        const dataWithDates = {
          ...rawData,
          operations: rawData.operations?.map((operation) => ({
            ...operation,
            createdAt: operation.createdAt
              ? new Date(operation.createdAt)
              : undefined,
            updatedAt: operation.updatedAt
              ? new Date(operation.updatedAt)
              : undefined,
          })),
        };
        const data = ListOperationsResponseSchema.parse(dataWithDates);
        set((state) => {
          const operationsMap = new Map(state.operations);
          data.operations.forEach((operation) => {
            operationsMap.set(operation.id, operation);
          });
          return { operations: operationsMap, listLoading: false };
        });
        return {
          ids: data.operations.map((operation) => operation.id),
          total: data.total,
        };
      }

      set({ listError: "Failed to fetch operations", listLoading: false });
      return { ids: [], total: 0 };
    } catch (error) {
      captureException(error as Error);
      set({ listError: "Failed to fetch operations", listLoading: false });
      return { ids: [], total: 0 };
    }
  },

  fetchOperation: async (id: string) => {
    set((state) => {
      const loadingStates = new Map(state.operationLoadingStates);
      const currentState = loadingStates.get(id) || getInitialLoadingState();
      loadingStates.set(id, {
        ...currentState,
        isLoadingGet: true,
        getError: null,
      });
      return { operationLoadingStates: loadingStates };
    });

    try {
      const response = await fetchApi(`operations/${id}`, "GET");

      if (response.ok && response.data) {
        // Parse dates from strings before validation
        const rawData = response.data as {
          operation: {
            createdAt?: string;
            updatedAt?: string;
            [key: string]: unknown;
          };
        };
        const dataWithDates = {
          ...rawData,
          operation: {
            ...rawData.operation,
            createdAt: rawData.operation.createdAt
              ? new Date(rawData.operation.createdAt)
              : undefined,
            updatedAt: rawData.operation.updatedAt
              ? new Date(rawData.operation.updatedAt)
              : undefined,
          },
        };
        const data = GetOperationResponseSchema.parse(dataWithDates);
        set((state) => {
          const operations = new Map(state.operations);
          const loadingStates = new Map(state.operationLoadingStates);
          const currentState =
            loadingStates.get(id) || getInitialLoadingState();
          operations.set(id, data.operation);
          loadingStates.set(id, {
            ...currentState,
            isLoadingGet: false,
            getError: null,
          });
          return { operations, operationLoadingStates: loadingStates };
        });
        return;
      }

      set((state) => {
        const loadingStates = new Map(state.operationLoadingStates);
        const currentState = loadingStates.get(id) || getInitialLoadingState();
        loadingStates.set(id, {
          ...currentState,
          isLoadingGet: false,
          getError: "Failed to fetch operation",
        });
        return { operationLoadingStates: loadingStates };
      });
    } catch (error) {
      captureException(error as Error);
      set((state) => {
        const loadingStates = new Map(state.operationLoadingStates);
        const currentState = loadingStates.get(id) || getInitialLoadingState();
        loadingStates.set(id, {
          ...currentState,
          isLoadingGet: false,
          getError: "Failed to fetch operation",
        });
        return { operationLoadingStates: loadingStates };
      });
    }
  },

  createOperation: async (data: CreateOperationParams) => {
    try {
      const response = await fetchApi("operations", "POST", {
        body: data,
      });

      if (response.ok && response.data) {
        const result = CreateOperationResponseSchema.parse(response.data);
        set((state) => {
          const operations = new Map(state.operations);
          operations.set(result.operation.id, result.operation);
          return { operations };
        });
        return { operation: result.operation, error: null };
      }

      return {
        operation: null,
        error: response.error ?? "Failed to create operation",
      };
    } catch (error) {
      captureException(error as Error);
      return { operation: null, error: "Failed to create operation" };
    }
  },

  updateOperation: async (id: string, data: UpdateOperationParams) => {
    set((state) => {
      const loadingStates = new Map(state.operationLoadingStates);
      const currentState = loadingStates.get(id) || getInitialLoadingState();
      loadingStates.set(id, {
        ...currentState,
        isLoadingSave: true,
        saveError: null,
      });
      return { operationLoadingStates: loadingStates };
    });

    try {
      const response = await fetchApi(`operations/${id}`, "PUT", {
        body: data,
      });

      if (response.ok && response.data) {
        // Parse dates from strings before validation
        const result = UpdateOperationResponseSchema.parse(response.data);
        set((state) => {
          const operations = new Map(state.operations);
          const loadingStates = new Map(state.operationLoadingStates);
          const currentState =
            loadingStates.get(id) || getInitialLoadingState();
          operations.set(id, result.operation);
          loadingStates.set(id, {
            ...currentState,
            isLoadingSave: false,
            saveError: null,
          });
          return { operations, operationLoadingStates: loadingStates };
        });
        return { operation: result.operation, error: null };
      }

      const errorMessage = response.error ?? "Failed to update operation";
      set((state) => {
        const loadingStates = new Map(state.operationLoadingStates);
        const currentState = loadingStates.get(id) || getInitialLoadingState();
        loadingStates.set(id, {
          ...currentState,
          isLoadingSave: false,
          saveError: errorMessage,
        });
        return { operationLoadingStates: loadingStates };
      });
      return { operation: null, error: errorMessage };
    } catch (error) {
      captureException(error as Error);
      set((state) => {
        const loadingStates = new Map(state.operationLoadingStates);
        const currentState = loadingStates.get(id) || getInitialLoadingState();
        loadingStates.set(id, {
          ...currentState,
          isLoadingSave: false,
          saveError: "Failed to update operation",
        });
        return { operationLoadingStates: loadingStates };
      });
      return { operation: null, error: "Failed to update operation" };
    }
  },

  deleteOperation: async (id: string) => {
    set((state) => {
      const loadingStates = new Map(state.operationLoadingStates);
      const currentState = loadingStates.get(id) || getInitialLoadingState();
      loadingStates.set(id, {
        ...currentState,
        isLoadingDelete: true,
        deleteError: null,
      });
      return { operationLoadingStates: loadingStates };
    });

    try {
      const response = await fetchApi(`operations/${id}`, "DELETE");

      if (response.ok) {
        set((state) => {
          const operations = new Map(state.operations);
          const loadingStates = new Map(state.operationLoadingStates);
          operations.delete(id);
          loadingStates.delete(id);
          return { operations, operationLoadingStates: loadingStates };
        });
        return true;
      }

      set((state) => {
        const loadingStates = new Map(state.operationLoadingStates);
        const currentState = loadingStates.get(id) || getInitialLoadingState();
        loadingStates.set(id, {
          ...currentState,
          isLoadingDelete: false,
          deleteError: "Failed to delete operation",
        });
        return { operationLoadingStates: loadingStates };
      });
      return false;
    } catch (error) {
      captureException(error as Error);
      set((state) => {
        const loadingStates = new Map(state.operationLoadingStates);
        const currentState = loadingStates.get(id) || getInitialLoadingState();
        loadingStates.set(id, {
          ...currentState,
          isLoadingDelete: false,
          deleteError: "Failed to delete operation",
        });
        return { operationLoadingStates: loadingStates };
      });
      return false;
    }
  },

  setOperationArchived: async (id: string, archived: boolean) => {
    set((state) => ({
      operationLoadingStates: patchLoadingState(state, id, {
        isLoadingArchive: true,
      }),
    }));

    const failure = archived
      ? "Failed to archive operation"
      : "Failed to unarchive operation";
    try {
      // Synced operations reject any other field alongside archived.
      const response = await fetchApi(`operations/${id}`, "PUT", {
        body: { archived },
      });

      if (response.ok && response.data) {
        const result = UpdateOperationResponseSchema.parse(response.data);
        set((state) => {
          const operations = new Map(state.operations);
          operations.set(id, result.operation);
          return {
            operations,
            operationLoadingStates: patchLoadingState(state, id, {
              isLoadingArchive: false,
            }),
          };
        });
        return { operation: result.operation, error: null };
      }

      set((state) => ({
        operationLoadingStates: patchLoadingState(state, id, {
          isLoadingArchive: false,
        }),
      }));
      return { operation: null, error: response.error ?? failure };
    } catch (error) {
      captureException(error as Error);
      set((state) => ({
        operationLoadingStates: patchLoadingState(state, id, {
          isLoadingArchive: false,
        }),
      }));
      return { operation: null, error: failure };
    }
  },
}));

/**
 * Returns a copy of the loading state map with one operation's state patched.
 */
function patchLoadingState(
  state: OperationsStore,
  id: string,
  patch: Partial<OperationLoadingState>,
): Map<string, OperationLoadingState> {
  const loadingStates = new Map(state.operationLoadingStates);
  const currentState = loadingStates.get(id) || getInitialLoadingState();
  loadingStates.set(id, { ...currentState, ...patch });
  return loadingStates;
}
